import React, { useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import './CircularLoading.scss';
import Typography from '@material-ui/core/Typography';

const polarToCartesian = (cx, cy, radius, deg) => {
  const rad = (deg - 90) * Math.PI / 180.0;

  return {
    x: cx + (radius * Math.cos(rad)),
    y: cy + (radius * Math.sin(rad)),
  };
};

const createSvgArc = (x, y, radius, startAngle, endAngle) => {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);

  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  // return `M ${start.x} start.y,
  //   "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y
  // ].join(" ");

  const d = [
    'M', start.x, start.y,
    'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y,
].join(' ');

  return d;
};

const makeKey = i => i;

const ROW_LAYOUT_WIDTH_THRESOLD = 350;

const CircularLoading = ({
  arcCount,
  period,
  style,
  children,
  strokeWidth,
  arc,
  circleSize,
  centered,
  message,
}) => {
  const [{ cx, cy, size, radius, arcs, width }, setSize] = useState({});
  const divElt = useRef();
  const arcEls = useRef(new Array(arcCount));
  const anim = useRef();
  const circleStyle = useMemo(() => ({ strokeWidth: `${strokeWidth}px` }), [strokeWidth]);

  const rowLayout = centered === true
    ? false
    : ((centered === false) || (width > ROW_LAYOUT_WIDTH_THRESOLD));

  useEffect(() => {
    const updateSize = () => {
      const w = divElt.current.offsetWidth || 0;
      const h = divElt.current.offsetHeight || 0;
      const s = rowLayout ? circleSize : Math.min(w, h);
      const r = (s - strokeWidth) / 2;
      const _cx = s / 2;
      const acs = new Array(arcCount);
      const inc = 360 / arcCount;
      for (let i = 0, startAng = 0; i < arcCount; i += 1) {
        acs[i] = createSvgArc(_cx, _cx, r, startAng, startAng + arc);
        startAng += inc;
      }
      setSize({
        width: w,
        height: h,
        size: s,
        cx: _cx,
        cy: _cx,
        radius: r,
        arcs: acs,
      });
    };
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, [circleSize, rowLayout]);

  useEffect(() => {
    let visibleArc = 0;
    let last;
    const animate = (timestamp) => {
      if (last === undefined) {
        last = timestamp;
      }
      const elapsed = timestamp - last;
      if (elapsed >= period) {
        last = timestamp;
        visibleArc = (visibleArc + 1) % arcCount;
        if (arcEls.current[0]) {
          for (let i = 0; i < arcCount; i += 1) {
            arcEls.current[i].style.visibility = i === visibleArc ? 'visible' : 'hidden';
          }
        }
      }
      anim.current = window.requestAnimationFrame(animate);
    };
    anim.current = window.requestAnimationFrame(animate);
    return () => {
      window.cancelAnimationFrame(anim.current);
    };
  }, [arcCount, period]);

  const content = (
    <div className="grb-circular-loading-content">
      {children}
      {!!message && (Array.isArray(message) ? message : [message]).map((m, i) => (
        <Typography key={makeKey(i)}>{m}</Typography>
      ))}
    </div>
  );

  const svg = size ? (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={rowLayout ? 'grb-svg-row-layout' : 'grb-svg-absolute'}
    >
      {arcs.map((a, i) => (
        <path
          d={a}
          key={makeKey(i)}
          className="grb-circular-loading-arc"
          ref={(elt) => { arcEls.current[i] = elt; }}
          style={circleStyle}
        />
      ))}
      <circle
        className="grb-circular-loading-circle"
        cx={cx}
        cy={cy}
        r={radius}
        style={circleStyle}
      />
    </svg>
  ) : undefined;

  return (
    <div ref={divElt} className="grb-circular-loading" style={style}>
      {rowLayout ? (
        <div className="grb-circular-loading-row">
          {svg}
          {content}
        </div>
      ) : (
        <React.Fragment>
          {content}
          {svg}
        </React.Fragment>
      )}
    </div>
  );
};

CircularLoading.propTypes = {
  children: PropTypes.node,
  arcCount: PropTypes.number,
  period: PropTypes.number,
  style: PropTypes.shape(),
  strokeWidth: PropTypes.number,
  arc: PropTypes.number,
  centered: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  circleSize: PropTypes.number,
  message: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]),
};

CircularLoading.defaultProps = {
  children: undefined,
  arcCount: 20,
  period: 50,
  style: undefined,
  strokeWidth: 8,
  arc: 50,
  centered: undefined,
  circleSize: 100, // In row mode
  message: undefined,
};

export default CircularLoading;
